import { Link, usePage } from '@inertiajs/react';
import {
    ChevronLeft,
    ChevronRight,
    Heart,
    Menu,
    Search,
    ShoppingBag,
    User,
    Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import MiniCart from '@/theme2/components/cart/MiniCart';
import CollectionsMegaMenu from '@/theme2/components/layout/CollectionsMegaMenu';
import { MobileNav } from '@/theme2/components/layout/MobileNav';
import SearchDialog from '@/theme2/components/search/SearchDialog';
import { useCart } from '@/theme2/hooks/useCart';
import { useWishlist } from '@/theme2/hooks/useWishlist';

export function Header() {
    const headerRef = useRef(null);
    const navRef = useRef(null);
    const { navigation = {}, auth = {}, settings = {} } = usePage().props;
    const user = auth.user ?? null;
    const mainNavigation = navigation.mainNavigation ?? [];
    const appLogo = settings.logo || null;
    const appName = settings.name || 'Captex';

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);
    const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const { wishlistCount } = useWishlist();
    const { cartCount } = useCart();

    const accountHref = user ? '/account' : '/login';

    const visibleNavigation = useMemo(
        () => mainNavigation.filter((item) => item?.label && item?.href),
        [mainNavigation],
    );

    const updateScrollState = () => {
        const nav = navRef.current;

        if (!nav) return;

        setCanScrollLeft(nav.scrollLeft > 0);
        setCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
    };

    useEffect(() => {
        updateScrollState();

        const nav = navRef.current;
        if (!nav) return undefined;

        nav.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);

        return () => {
            nav.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setIsSearchOpen(true);
            }

            if (event.key === 'Escape') {
                setIsSearchOpen(false);
                setIsMiniCartOpen(false);
                setIsCollectionsOpen(false);
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (headerRef.current && !headerRef.current.contains(event.target)) {
                setIsCollectionsOpen(false);
            }
        };

        document.addEventListener('pointerdown', handleOutsideClick);

        return () => document.removeEventListener('pointerdown', handleOutsideClick);
    }, []);

    const closeOverlays = () => {
        setIsCollectionsOpen(false);
        setIsMobileMenuOpen(false);
    };

    const scrollNav = (direction) => {
        const nav = navRef.current;

        if (!nav) return;

        const amount = Math.max(nav.clientWidth * 0.55, 240);

        nav.scrollBy({
            left: direction === 'left' ? -amount : amount,
            behavior: 'smooth',
        });
    };

    return (
        <header
            ref={headerRef}
            className="sticky top-0 z-50 border-b border-rose-100 bg-white/95 text-foreground shadow-[0_10px_40px_rgba(191,24,91,0.08)] backdrop-blur-xl"
        >
            <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex min-h-[72px] items-center gap-3">
                    <Link
                        href="/"
                        onClick={closeOverlays}
                        className="group flex shrink-0 items-center gap-3 rounded-full pr-2 transition hover:bg-[#fff3f7]"
                    >
                        {appLogo ? (
                            <img
                                src={appLogo}
                                alt={appName}
                                className="size-12 rounded-[1.15rem] border border-rose-100 bg-white object-contain p-1.5 shadow-[0_12px_30px_rgba(0,0,0,0.08)] transition group-hover:scale-[1.02]"
                            />
                        ) : (
                            <div className="flex size-12 items-center justify-center rounded-[1.15rem] border border-rose-100 bg-[#f51b75] font-black text-white shadow-[0_12px_30px_rgba(245,27,117,0.22)] transition group-hover:scale-[1.02]">
                                CT
                            </div>
                        )}

                        <span className="hidden sm:block">
                            <span className="block text-[10px] font-black uppercase tracking-[0.34em] text-[#d80b67]/55">
                                Captex
                            </span>
                            <span className="block text-base font-black uppercase tracking-[-0.03em] text-slate-900">
                                {appName}
                            </span>
                        </span>
                    </Link>

                    <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
                        <nav
                            ref={navRef}
                            className="scrollbar-hide flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap rounded-full border border-rose-100 bg-[#fff7fa] px-2 py-1 shadow-[0_10px_30px_rgba(245,27,117,0.06)]"
                            aria-label="Primary navigation"
                        >
                            {visibleNavigation.map((item) => {
                                const pillClasses = item.highlight
                                    ? 'bg-[#f51b75] text-white shadow-[0_10px_20px_rgba(245,27,117,0.18)]'
                                    : item.accent
                                        ? 'bg-white text-[#d80b67] ring-1 ring-[#f5b4c8]'
                                        : 'text-slate-700 hover:bg-white hover:text-[#d80b67]';

                                if (item.dropdown) {
                                    return (
                                        <button
                                            key={item.label}
                                            type="button"
                                            onClick={() => setIsCollectionsOpen((value) => !value)}
                                            className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] transition ${pillClasses}`}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                }

                                return (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        onClick={closeOverlays}
                                        className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] transition ${pillClasses}`}
                                    >
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>

                    <div className="ml-auto flex items-center gap-2 sm:gap-3">
                        <button
                            type="button"
                            aria-label="Search"
                            onClick={() => {
                                setIsSearchOpen(true);
                                closeOverlays();
                            }}
                            className="inline-flex min-h-[44px] w-full max-w-[46px] items-center justify-center rounded-full bg-[#f51b75] p-2.5 text-white shadow-[0_14px_32px_rgba(245,27,117,0.25)] transition hover:-translate-y-0.5 hover:bg-[#d80b67] md:max-w-[240px] md:justify-between md:px-4 md:py-2.5"
                        >
                            <span className="inline-flex items-center gap-2 text-sm text-white/90">
                                <Search className="size-4 shrink-0" />
                                <span className="hidden md:inline">Search products</span>
                            </span>

                            <kbd className="hidden rounded bg-white/18 px-2 py-0.5 text-[10px] font-semibold text-white md:inline">
                                Ctrl K
                            </kbd>
                        </button>

                        <Link
                            href="/wishlist"
                            aria-label="Wishlist"
                            onClick={closeOverlays}
                            className="relative inline-flex items-center justify-center rounded-full border border-rose-100 bg-white p-2.5 text-[#d80b67] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#fff3f7]"
                        >
                            <Heart className="size-5" />
                            {wishlistCount > 0 ? <CounterBadge count={wishlistCount} /> : null}
                        </Link>

                        <Link
                            href={accountHref}
                            aria-label={user ? `${user.name} account` : 'Account'}
                            onClick={closeOverlays}
                            className="hidden items-center gap-2 rounded-full border border-rose-100 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-[#fff3f7] sm:inline-flex"
                        >
                            {user ? (
                                <span className="flex size-7 items-center justify-center rounded-full bg-slate-900 text-[11px] font-black uppercase text-white">
                                    {getInitials(user.name)}
                                </span>
                            ) : (
                                <User className="size-4" />
                            )}
                            <span className="hidden lg:inline">{user ? 'Account' : 'Login'}</span>
                        </Link>

                        <button
                            type="button"
                            aria-label="Cart"
                            onClick={() => {
                                setIsMiniCartOpen(true);
                                closeOverlays();
                            }}
                            className="relative inline-flex items-center justify-center rounded-full border border-rose-100 bg-white p-2.5 text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-[#fff3f7]"
                        >
                            <ShoppingBag className="size-5" />
                            {cartCount > 0 ? <CounterBadge count={cartCount} /> : null}
                        </button>

                        <button
                            type="button"
                            aria-label="Open menu"
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="inline-flex items-center justify-center rounded-full border border-rose-100 bg-white p-2.5 text-slate-900 shadow-sm transition hover:bg-[#fff3f7] lg:hidden"
                        >
                            <Menu className="size-5" />
                        </button>
                    </div>
                </div>

                <div className="mt-3 flex items-center gap-2 lg:hidden">
                    <button
                        type="button"
                        onClick={() => scrollNav('left')}
                        disabled={!canScrollLeft}
                        aria-label="Scroll navigation left"
                        className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-rose-100 bg-white text-slate-700 shadow-sm disabled:opacity-40"
                    >
                        <ChevronLeft className="size-4" />
                    </button>

                    <nav
                        ref={navRef}
                        className="scrollbar-hide flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap rounded-full border border-rose-100 bg-[#fff7fa] px-2 py-1"
                        aria-label="Mobile primary navigation"
                    >
                        {visibleNavigation.map((item) => (
                            <Link
                                key={item.label}
                                href={item.href}
                                onClick={closeOverlays}
                                className="rounded-full border border-rose-100 bg-white px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-700"
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <button
                        type="button"
                        onClick={() => scrollNav('right')}
                        disabled={!canScrollRight}
                        aria-label="Scroll navigation right"
                        className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-rose-100 bg-white text-slate-700 shadow-sm disabled:opacity-40"
                    >
                        <ChevronRight className="size-4" />
                    </button>
                </div>
            </div>

            <CollectionsMegaMenu
                open={isCollectionsOpen}
                onClose={() => setIsCollectionsOpen(false)}
            />

            <SearchDialog
                open={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
            />

            <MiniCart
                open={isMiniCartOpen}
                onClose={() => setIsMiniCartOpen(false)}
            />

            <MobileNav
                open={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
            />
        </header>
    );
}

function CounterBadge({ count }) {
    return (
        <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-[#f51b75] px-1.5 py-0.5 text-[10px] font-black leading-none text-white shadow-[0_8px_18px_rgba(245,27,117,0.3)]">
            {count > 99 ? '99+' : count}
        </span>
    );
}

function getInitials(name = '') {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
}
